import { useMemo, useState } from 'react';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import Select, { Option } from '@ingka/select';
import DatePickerDisplay from 'components/TimeFilter/DatePickerDisplay';
import { ContractType } from 'types/scenario';
import noop from 'lodash/noop';
import Toggle from '@ingka/toggle';
import { displayContractRangeWithDescription, getShortCC } from 'utils/text';
import useData from 'hooks/useData';
import { ContractRange, EmploymentType } from 'types/coworker';
import { sortContractRangeDesc } from 'utils/sorting';
import { useNestedTranslation } from 'hooks/useNestedTranslation';
import classes from './AddCoworker.module.scss';

type TypeAddCoworkerPanel = {
    costCentreProps: {
        costCentre: string | undefined,
        costCentreOptions: {
            costCentre: string;
            costCentreDescription: string;
        }[];
        handleChangeCostCentre: (e: React.ChangeEvent<HTMLSelectElement>) => void,
        validCostCentre: boolean,
    };
    contractHoursProps: {
        contractRange: ContractRange | undefined,
        contractHours: string | number | undefined,
        handleChangeContractHours: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void,
        validContractHours: boolean,
    };
    contractDatesProps: {
        startDate: string | null,
        endDate: string | null,
        handleChangeEndDate: (e: React.ChangeEvent<HTMLInputElement>) => void,
        handleChangeStartDate: (e: React.ChangeEvent<HTMLInputElement>) => void,
        minimumStartDate: string
        validDates: boolean,
    };
    contractTypeProps: {
        contractType: ContractType,
        contractOptions: string[],
        handleChangeContractType: (e: React.ChangeEvent<HTMLSelectElement>) => void,
        isPermanentContract: (ct: ContractType) => boolean,
    };
    employmentTypeProps: {
        employmentType: string | undefined,
        handleChangeEmploymentType: (e: React.ChangeEvent<HTMLSelectElement>) => void,
    };
    coworkerNameProps: {
        coworkerName: string | undefined,
        handleChangeCoworkerName: (e: React.ChangeEvent<HTMLInputElement>) => void,
        validCoworkerName: boolean
    };
    unitName: string
};

const AddCoworkerPanel = ({
    costCentreProps,
    contractHoursProps,
    contractDatesProps,
    contractTypeProps,
    coworkerNameProps,
    employmentTypeProps,
    unitName
}:TypeAddCoworkerPanel) => {
    const t = useNestedTranslation('addCoworker');
    const { contractHours, contractRange, handleChangeContractHours, validContractHours } = contractHoursProps;
    const { costCentre, costCentreOptions, handleChangeCostCentre, validCostCentre } = costCentreProps;
    const { coworkerName, handleChangeCoworkerName, validCoworkerName } = coworkerNameProps;
    const { contractType, contractOptions, handleChangeContractType, isPermanentContract } = contractTypeProps;
    const { startDate, endDate, handleChangeEndDate, handleChangeStartDate, minimumStartDate, validDates } = contractDatesProps;
    const { employmentType, handleChangeEmploymentType } = employmentTypeProps;
    const [toggleIndex, setToggleIndex] = useState<number | null>(null);
    const { config } = useData();

    const ccDropDownList = useMemo(() => {
        //* Remove duplicate costCentreCode entries from orgSelection due to OPP OM API
        const uniqueSelectedCostCentres = costCentreOptions
            .filter((cc, index, self) => index === self.findIndex(other => other.costCentre === cc.costCentre));
        if (toggleIndex || null) {
            return uniqueSelectedCostCentres
                ?.sort((current, next) => Number(getShortCC(current.costCentre)) - Number(getShortCC(next.costCentre)));
        }

        return uniqueSelectedCostCentres
            ?.sort((current, next) => current.costCentreDescription
                .localeCompare(next.costCentreDescription));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggleIndex]);

    return (
        <div className={classes['new-coworker-container']} data-testid="add-coworker-modal-container">
            <div className={classes['heading-row']}>{t('newCoworker')}</div>
            <div className={classes['input-field']}>
                <FormField
                    valid={validCoworkerName}
                    shouldValidate={!validCoworkerName}
                    validation={{
                        msg: t('enterCoworkerName')
                    }}
                >
                    <InputField
                        data-testid="new-coworker-name-input"
                        label={t('name')}
                        value={coworkerName}
                        id="new-coworker-name-input"
                        type="text"
                        onChange={handleChangeCoworkerName}
                        autoFocus
                    />
                </FormField>
            </div>
            <div className={classes['select-dropdown']}>
                <Select
                    data-testid="new-coworker-contract-type"
                    id="new-coworker-contract-type"
                    label={t('contractType')}
                    value={contractType}
                    hintText=""
                    onChange={handleChangeContractType}
                >
                    {contractOptions.map(val => (
                        <Option key={`contract-type-${val}`} value={val} name={t(val)} />
                    ))}
                </Select>
            </div>
            { !config?.isContractRangeSupported && (
            <div className={classes['input-field']}>
                <FormField
                    valid={validContractHours}
                    shouldValidate={!validContractHours}
                    validation={{
                        msg: t('enterContractHours')
                    }}
                >
                    <InputField
                        label={t('contractHours')}
                        data-testid="new-coworker-contract-hours"
                        id="new-coworker-contract-hours"
                        type="number"
                        inputMode="numeric"
                        autoComplete="off"
                        value={contractHours ?? ''}
                        onChange={handleChangeContractHours}
                    />
                </FormField>
            </div>
            )}
            { config?.isContractRangeSupported && (
                <div className={classes['select-dropdown']}>
                    <FormField
                        valid={validContractHours}
                        shouldValidate={!validContractHours}
                        validation={{
                            msg: t('selectContractRange')
                        }}
                    >
                        <Select
                            data-testid="new-coworker-contract-ranges"
                            id="new-coworker-contract-ranges"
                            label={t('contractHours')}
                            value={contractRange?.type}
                            hintText={t('selectContractRange')}
                            onChange={handleChangeContractHours}
                        >
                            {config.contractRanges
                                .sort(sortContractRangeDesc)
                                .map(cr => (
                                    <Option
                                        key={`contract-range-${cr.type}`}
                                        value={cr.type}
                                        name={displayContractRangeWithDescription(cr)}
                                    />
                                ))}
                        </Select>
                    </FormField>
                </div>

            )}
            <div className={classes['date-field']}>
                <DatePickerDisplay
                    data-testId="new-coworker-start-time"
                    header={t('selectStartWeek')}
                    inputType="week"
                    inputValue={startDate || ''}
                    handleChange={handleChangeStartDate}
                    handleBlur={noop}
                    minValue={minimumStartDate || null}
                    maxValue={endDate || null}
                    invalid={!validDates}
                />
            </div>
            {!isPermanentContract(contractType)
                        && (
                            <div className={classes['date-field']}>
                                <DatePickerDisplay
                                    data-testId="new-coworker-end-time"
                                    header={t('selectEndWeek')}
                                    inputType="week"
                                    inputValue={endDate || ''}
                                    handleChange={handleChangeEndDate}
                                    handleBlur={noop}
                                    minValue={startDate || null}
                                    invalid={!validDates}
                                />
                            </div>
                        )}
            <div className={classes['select-dropdown']}>
                <Select
                    id="new-coworker-employment-type"
                    data-testid="new-coworker-employment-type"
                    label={t('employmentType')}
                    value={employmentType}
                    hintText=""
                    onChange={handleChangeEmploymentType}
                >
                    {Object.keys(EmploymentType)
                        .filter(key => Number.isNaN(Number(key)))
                        .map(key => (
                            <Option key={`employment-type-${key}`} value={key} name={t(key)} />
                        ))}
                </Select>
            </div>
            <div className={classes['select-dropdown-toggle']}>
                <Toggle
                    buttons={[{
                        text: t.global('aToZ'),
                        'aria-label': t('sortCcAlphabetically')
                    }, {
                        text: '0-9',
                        'aria-label': t('sortCcNumerically')
                    }]}
                    fluid={false}
                    activeIndex={toggleIndex ?? 0}
                    onClick={(event, index) => {
                        setToggleIndex(index);
                    }}
                />
            </div>
            {ccDropDownList
                        && (
                            <div className={classes['select-dropdown']}>
                                <FormField
                                    valid={validCostCentre}
                                    shouldValidate={!validCostCentre}
                                    validation={{
                                        msg: t('selectCostCentre')
                                    }}
                                >
                                    <Select
                                        id="new-coworker-cost-center"
                                        data-testid="new-coworker-cost-center"
                                        label={t.global('costCentre')}
                                        hintText={t('selectCostCentre')}
                                        value={costCentre}
                                        onChange={handleChangeCostCentre}
                                    >
                                        {ccDropDownList?.map(cc => (
                                            <Option
                                                key={cc.costCentre}
                                                value={cc.costCentre}
                                                name={`${getShortCC(cc.costCentre)} - ${cc.costCentreDescription}`}
                                            />
                                        ))}
                                    </Select>
                                </FormField>
                            </div>
                        )}
            <div className={classes['select-dropdown']}>
                <Select
                    id="new-coworker-unit"
                    data-testid="new-coworker-unit"
                    label={t.global('unit')}
                    hintText=""
                    disabled
                >
                    <Option
                        value={unitName}
                        name={unitName}
                    />
                </Select>
            </div>
        </div>

    );
};

export default AddCoworkerPanel;
